import React from "react";

// Single source of truth for how an option tile renders. Three style
// variants drive the visual:
//   - default (the legacy image-grid card with image + label below)
//   - compact   (small bordered button — "ARABIC / ENGLISH", "SOCIAL")
//   - big_text  (huge centered text — "AA / aa / Aa" capitalization)
//   - icon      (small circular icon + label below — "CLEAN & CLASSIC")
//
// The variant is picked from option.metadata.style (admin-set per option)
// or question.config.option_style (admin-set on the question to apply to
// every option). Option-level overrides win.
export const OptionCard = ({ option, selected, onClick, lang, questionStyle }) => {
  const meta = option.metadata || {};
  const style = meta.style || questionStyle || "default";
  const label = lang === "ar" && option.label_arabic ? option.label_arabic : option.label;
  const tip = lang === "ar" && option.tooltip_arabic ? option.tooltip_arabic : option.tooltip;

  // ---- compact: small bordered button ------------------------------------
  if (style === "compact") {
    return (
      <button
        type="button"
        onClick={onClick}
        title={tip || undefined}
        style={{
          padding: "6px 18px",
          border: "1px solid " + (selected ? "#000" : "#bbb"),
          background: selected ? "#000" : "#fff",
          color: selected ? "#fff" : "#000",
          borderRadius: 3,
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          letterSpacing: 0.5,
          textTransform: "uppercase",
        }}
      >
        {label}
      </button>
    );
  }

  // ---- big_text: AA / aa / Aa preview cards ------------------------------
  if (style === "big_text") {
    const previewText = meta.preview_text || label;
    return (
      <button
        type="button"
        onClick={onClick}
        title={tip || undefined}
        style={{
          width: "100%",
          aspectRatio: "1.6 / 1",
          border: "1px solid " + (selected ? "#000" : "#ddd"),
          background: "#fff",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "min(8vw, 80px)",
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: selected ? "0 0 0 2px #000 inset" : "none",
        }}
      >
        {previewText}
      </button>
    );
  }

  // ---- icon: small circular icon + label below --------------------------
  if (style === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        title={tip || undefined}
        style={{
          background: "transparent",
          border: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          padding: 8,
        }}
      >
        {option.image ? (
          <img
            src={option.image}
            alt={label}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              objectFit: "cover",
              boxShadow: selected ? "0 0 0 2px #000" : "none",
            }}
          />
        ) : (
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#ccc",
              boxShadow: selected ? "0 0 0 2px #000" : "none",
            }}
          />
        )}
        <span
          style={{
            padding: "4px 10px",
            border: "1px solid #000",
            borderRadius: 3,
            fontSize: 11,
            fontWeight: 600,
            background: selected ? "#000" : "#fff",
            color: selected ? "#fff" : "#000",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </span>
      </button>
    );
  }

  // ---- default: legacy image-grid card with optional (i) tooltip dot -----
  return (
    <button
      type="button"
      onClick={onClick}
      className={`dq-option ${selected ? "selected" : ""}`}
      title={tip || undefined}
      style={{ position: "relative" }}
    >
      {tip ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#222",
            color: "#fff",
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontFamily: "serif",
          }}
        >
          i
        </span>
      ) : null}
      {option.image ? <img src={option.image} alt={option.label} /> : null}
      <span className="dq-label">{label}</span>
    </button>
  );
};
